import { useState } from 'react';
import { AppShell } from '../components/AppShell';
import { useAuth } from '../context/AuthContext';
import { saveDateOfBirth, saveParentEmail } from '../lib/access';
import { authHeaders } from '../lib/supabase';

// Age capture screen. Shown after a user signs in for the first time (no DOB
// recorded yet). Under-13 also captures parent email; then the parental-consent
// flow kicks in on the next screen.
//
// Compliance: UK GDPR Article 8 requires verifiable parental consent for
// under-13s. Storing DOB + computing age band + capturing parent email is the
// first step of that flow. The parental consent request email is sent next.
export function AgeCaptureScreen() {
  const { user, refreshProfile } = useAuth();
  const [dob, setDob] = useState('');
  const [parentEmail, setParentEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Live-compute age to decide whether to reveal the parent email field.
  const computedAge = computeYears(dob);
  const isUnder13 = computedAge !== null && computedAge < 13;
  const needsParentEmail = isUnder13;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!user) {
      setError('You need to be signed in.');
      return;
    }
    if (!dob) {
      setError('Please enter your date of birth.');
      return;
    }
    if (computedAge === null || computedAge < 5 || computedAge > 100) {
      setError('That date does not look right. Please check and try again.');
      return;
    }
    if (needsParentEmail && !isValidEmail(parentEmail)) {
      setError('Please enter your parent or guardian&apos;s email address so we can ask them for permission.');
      return;
    }
    setBusy(true);
    try {
      const savedDob = await saveDateOfBirth(user.id, dob);
      if (!savedDob) throw new Error('Could not save date of birth. Please try again.');
      if (needsParentEmail) {
        const savedParent = await saveParentEmail(user.id, parentEmail);
        if (!savedParent) throw new Error('Could not save parent email. Please try again.');
        // Kick off the parental consent email (fire-and-forget - the pending
        // screen will show a Resend button if it never arrives).
        void authHeaders().then((headers) =>
          fetch('/api/consent/send-parental-request', {
            method: 'POST',
            headers,
            body: JSON.stringify({ parentEmail: parentEmail.trim().toLowerCase() }),
          }),
        );
      }
      await refreshProfile();
      // AuthContext gate will now route: under-13 -> ParentalConsentPending,
      // 13+ -> TermsAcceptance -> app.
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AppShell title="A quick question">
      <div className="px-5 pt-6 pb-8 max-w-md mx-auto">
        <h1 className="text-2xl font-semibold tracking-tight text-ink">Let&apos;s make sure this is right for you</h1>
        <p className="mt-3 text-sm text-ink/70 leading-relaxed">
          We ask everyone their age when they sign in for the first time. If you&apos;re under 13, we&apos;ll
          ask your parent or guardian to say it&apos;s OK before you can use the app. This is the law in the UK
          and we take it seriously.
        </p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-ink mb-1.5" htmlFor="dob">
              Your date of birth
            </label>
            <input
              id="dob"
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              max={new Date().toISOString().slice(0, 10)}
              required
              className="w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-base text-ink focus:outline-none focus:ring-2 focus:ring-accent/40"
            />
            {computedAge !== null && (
              <p className="mt-1.5 text-xs text-ink/55">
                You are {computedAge} years old.
                {isUnder13 && ' We&apos;ll email your parent or guardian for permission.'}
              </p>
            )}
          </div>

          {needsParentEmail && (
            <div>
              <label className="block text-sm font-medium text-ink mb-1.5" htmlFor="parentEmail">
                Your parent or guardian&apos;s email
              </label>
              <input
                id="parentEmail"
                type="email"
                inputMode="email"
                autoComplete="email"
                value={parentEmail}
                onChange={(e) => setParentEmail(e.target.value)}
                placeholder="parent@example.com"
                required
                className="w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-base text-ink focus:outline-none focus:ring-2 focus:ring-accent/40"
              />
              <p className="mt-1.5 text-xs text-ink/55">
                We&apos;ll email them a link to say it&apos;s OK for you to use the app. You&apos;ll be able to
                use it as soon as they click the link.
              </p>
            </div>
          )}

          {error && (
            <div role="alert" className="rounded-xl bg-warm border border-amber-300 text-ink px-4 py-3 text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full inline-flex items-center justify-center bg-accent text-white px-6 py-3 rounded-full font-medium hover:brightness-105 transition disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {busy ? 'Saving...' : needsParentEmail ? 'Send my parent the email' : 'Continue'}
          </button>
        </form>

        <p className="mt-6 text-xs text-ink/50 leading-relaxed">
          We use your date of birth to make sure the app is set up in a way that&apos;s right for your age.
          We don&apos;t share it with anyone else. Full details in our{' '}
          <a href="/privacy" className="underline">Privacy Policy</a>.
        </p>
      </div>
    </AppShell>
  );
}

function computeYears(isoDate: string): number | null {
  if (!isoDate) return null;
  const dob = new Date(isoDate);
  if (isNaN(dob.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) age--;
  return age;
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}
